import { useQuery, gql, QueryHookOptions, useMutation, MutationHookOptions } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLQueryAccessory_GetAccessoryArgs,
  GQLQueryAccessory_GetAccessoriesByProducerArgs,
  GQLAccessoryAssetConnection,
  GQLQueryAccessory_GetAccessoriesByMerchantArgs,
  GQLQueryAccessory_GetAccessoriesByPlatformArgs,
  GQLAccessoryAsset,
  GQLJobSummary,
  GQLQueryAccessory_ImportAccessoryModelCadFileJobsArgs,
  GQLMutationAccessory_ImportAccessoryModelCadFileArgs,
  GQLMutationAccessory_ImportAccessoryModelIconFileArgs,
  GQLMutationAccessory_CreateAccessoryImageArgs,
  GQLMedia,
  GQLMutationAccessory_CreateAccessoryModelArgs,
  GQLMutationAccessory_UpdateAccessoryModelArgs,
  GQLMutationAccessory_CreateAccessoryArgs,
  GQLMutationAccessory_UpdateAccessoryArgs,
  GQLQueryAccessory_GetAccessoriesArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { ACCESSORY_ASSET_FIELDS } from './fragments/accessory';
import { MEDIA_FIELDS } from './fragments/common';
import { JOB_SUMMARY_FIELDS } from './fragments/job';
import { OperationsHelper } from './OperationHelper';

export class AccessoryOperation {
  accessoryAssetArrayFromResponse(data: any): GQLAccessoryAsset[] {
    if (data.Accessory_getAccessories !== undefined) {
      return data.Accessory_getAccessories;
    }

    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'accessories');
  }

  accessoryAssetFromResponse(data: any): GQLAccessoryAsset {
    if (data.Accessory_getAccessory !== undefined) {
      return data.Accessory_getAccessory;
    }
    throw new Exception(`Cannot handle input data accessoryAsset`);
  }

  jobSummaryArrayFromResponse(data: any): GQLJobSummary[] {
    if (data === undefined) return [];
    if (data.Accessory_importAccessoryModelCadFileJobs !== undefined) {
      return data.Accessory_importAccessoryModelCadFileJobs;
    }
    throw new Exception(`Cannot handle input data job summary`);
  }

  getAccessoriesByPlatform(
    options: QueryHookOptions<
      GQLAccessoryAssetConnection,
      GQLQueryAccessory_GetAccessoriesByPlatformArgs
    > = {}
  ) {
    const query = gql`
      ${ACCESSORY_ASSET_FIELDS}
      query getAccessoriesByPlatform($pageSize: Int = 10) {
        Accessory_getAccessoriesByPlatform(pageSize: $pageSize) {
          cursor
          hasMore
          accessories {
            ...AccessoryAssetFields
          }
        }
      }
    `;
    return useQuery<GQLAccessoryAssetConnection, GQLQueryAccessory_GetAccessoriesByPlatformArgs>(query, {
      ...options
    });
  }

  getAccessoriesByProducer(
    args: GQLQueryAccessory_GetAccessoriesByProducerArgs,
    options: QueryHookOptions<
      GQLAccessoryAssetConnection,
      GQLQueryAccessory_GetAccessoriesByProducerArgs
    > = {}
  ) {
    const query = gql`
      ${ACCESSORY_ASSET_FIELDS}
      query getAccessoriesByProducer($producerPartyId: ID!) {
        Accessory_getAccessoriesByProducer(producerPartyId: $producerPartyId) {
          cursor
          hasMore
          accessories {
            ...AccessoryAssetFields
          }
        }
      }
    `;
    return useQuery<GQLAccessoryAssetConnection, GQLQueryAccessory_GetAccessoriesByProducerArgs>(query, {
      ...options,
      variables: args
    });
  }

  getAccessoriesByMerchant(
    args: GQLQueryAccessory_GetAccessoriesByMerchantArgs,
    options: QueryHookOptions<
      GQLAccessoryAssetConnection,
      GQLQueryAccessory_GetAccessoriesByMerchantArgs
    > = {}
  ) {
    const query = gql`
      ${ACCESSORY_ASSET_FIELDS}
      query getAccessoriesByMerchant($merchantPartyId: ID!, $pageSize: Int = 10) {
        Accessory_getAccessoriesByMerchant(merchantPartyId: $merchantPartyId, pageSize: $pageSize) {
          cursor
          hasMore
          accessories {
            ...AccessoryAssetFields
          }
        }
      }
    `;
    return useQuery<GQLAccessoryAssetConnection, GQLQueryAccessory_GetAccessoriesByMerchantArgs>(query, {
      ...options,
      variables: args
    });
  }

  createAccessoryModel(
    options: MutationHookOptions<GQLAccessoryAsset, GQLMutationAccessory_CreateAccessoryModelArgs> = {}
  ) {
    const mutation = gql`
      ${ACCESSORY_ASSET_FIELDS}
      mutation createAccessoryModel($accessoryId: ID!, $input: AccessoryModelInput!) {
        Accessory_createAccessoryModel(accessoryId: $accessoryId, input: $input) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useMutation<GQLAccessoryAsset, GQLMutationAccessory_CreateAccessoryModelArgs>(mutation, options);
  }

  updateAccessoryModel(
    options: MutationHookOptions<GQLAccessoryAsset, GQLMutationAccessory_UpdateAccessoryModelArgs> = {}
  ) {
    const mutation = gql`
      ${ACCESSORY_ASSET_FIELDS}
      mutation updateAccessoryModel($accessoryId: ID!, $input: AccessoryModelInput!, $accessoryModelId: ID!) {
        Accessory_updateAccessoryModel(
          accessoryId: $accessoryId
          accessoryModelId: $accessoryModelId
          input: $input
        ) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useMutation<GQLAccessoryAsset, GQLMutationAccessory_UpdateAccessoryModelArgs>(mutation, options);
  }

  createAccessory(
    options: MutationHookOptions<GQLAccessoryAsset, GQLMutationAccessory_CreateAccessoryArgs> = {}
  ) {
    const mutation = gql`
      ${ACCESSORY_ASSET_FIELDS}
      mutation createAccessory($input: AccessoryAssetInput!) {
        Accessory_createAccessory(input: $input) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useMutation<GQLAccessoryAsset, GQLMutationAccessory_CreateAccessoryArgs>(mutation, options);
  }

  updateAccessory(
    options: MutationHookOptions<GQLAccessoryAsset, GQLMutationAccessory_UpdateAccessoryArgs> = {}
  ) {
    const mutation = gql`
      ${ACCESSORY_ASSET_FIELDS}
      mutation updateAccessory($input: AccessoryAssetInput!, $accessoryId: ID!) {
        Accessory_updateAccessory(accessoryId: $accessoryId, input: $input) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useMutation<GQLAccessoryAsset, GQLMutationAccessory_UpdateAccessoryArgs>(mutation, options);
  }

  getAccessories(
    args: GQLQueryAccessory_GetAccessoriesArgs,
    options: QueryHookOptions<GQLAccessoryAsset[], GQLQueryAccessory_GetAccessoriesArgs> = {}
  ) {
    const query = gql`
      ${ACCESSORY_ASSET_FIELDS}
      query getAccessories($accessoryIds: [ID!]!) {
        Accessory_getAccessories(accessoryIds: $accessoryIds) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useQuery<GQLAccessoryAsset[], GQLQueryAccessory_GetAccessoriesArgs>(query, {
      ...options,
      variables: args
    });
  }

  getAccessory(
    args: GQLQueryAccessory_GetAccessoryArgs,
    options: QueryHookOptions<GQLAccessoryAsset, GQLQueryAccessory_GetAccessoryArgs> = {}
  ) {
    const query = gql`
      ${ACCESSORY_ASSET_FIELDS}
      query getAccessory($accessoryId: ID!) {
        Accessory_getAccessory(accessoryId: $accessoryId) {
          ...AccessoryAssetFields
        }
      }
    `;
    return useQuery<GQLAccessoryAsset, GQLQueryAccessory_GetAccessoryArgs>(query, {
      ...options,
      variables: args
    });
  }

  importAccessoryModelCadFileJobs(
    args: GQLQueryAccessory_ImportAccessoryModelCadFileJobsArgs,
    options: QueryHookOptions<GQLJobSummary[], GQLQueryAccessory_ImportAccessoryModelCadFileJobsArgs> = {}
  ) {
    const query = gql`
      ${JOB_SUMMARY_FIELDS}
      query Accessory_importAccessoryModelCadFileJobs($accessoryId: ID!, $accessoryModelId: ID!) {
        Accessory_importAccessoryModelCadFileJobs(
          accessoryId: $accessoryId
          accessoryModelId: $accessoryModelId
        ) {
          ...JobSummaryFields
        }
      }
    `;
    return useQuery<GQLJobSummary[], GQLQueryAccessory_ImportAccessoryModelCadFileJobsArgs>(query, {
      ...options,
      variables: args
    });
  }

  importAccessoryModelCadFile(
    options: MutationHookOptions<Boolean, GQLMutationAccessory_ImportAccessoryModelCadFileArgs> = {}
  ) {
    const mutation = gql`
      mutation Accessory_importAccessoryModelCadFile(
        $accessoryId: ID!
        $accessoryModelId: ID!
        $file: Upload!
      ) {
        Accessory_importAccessoryModelCadFile(
          accessoryId: $accessoryId
          accessoryModelId: $accessoryModelId
          file: $file
        )
      }
    `;
    return useMutation<Boolean, GQLMutationAccessory_ImportAccessoryModelCadFileArgs>(mutation, options);
  }

  importAccessoryModelIconFile(
    options: MutationHookOptions<Boolean, GQLMutationAccessory_ImportAccessoryModelIconFileArgs> = {}
  ) {
    const mutation = gql`
      mutation importAccessoryModelIcon($accessoryId: ID!, $accessoryModelId: ID!, $file: Upload!) {
        Accessory_importAccessoryModelIconFile(
          accessoryId: $accessoryId
          accessoryModelId: $accessoryModelId
          file: $file
        )
      }
    `;
    return useMutation<Boolean, GQLMutationAccessory_ImportAccessoryModelIconFileArgs>(mutation, options);
  }

  createAccessoryImage(
    options: MutationHookOptions<GQLMedia, GQLMutationAccessory_CreateAccessoryImageArgs> = {}
  ) {
    const mutation = gql`
      ${MEDIA_FIELDS}
      mutation createAccessoryImage(
        $accessoryId: ID!
        $accessoryConfiguration: AccessoryConfigurationInput!
        $file: Upload!
      ) {
        Accessory_createAccessoryImage(
          accessoryId: $accessoryId
          accessoryConfiguration: $accessoryConfiguration
          file: $file
        ) {
          ...MediaFields
        }
      }
    `;
    return useMutation<GQLMedia, GQLMutationAccessory_CreateAccessoryImageArgs>(mutation, options);
  }
}
